import type { SimulationPlaybackCluster } from './simulationPlayback';

export type FractureRenderStage = {
  stage_number: number;
  perf_md: number;
};
export type FractureRenderCluster = Pick<
  SimulationPlaybackCluster,
  'clusterId' | 'clusterIndex' | 'stageId' | 'perforation' | 'hasSurfaceGeometry'
>;

export type FractureRenderTemplate = {
  id: string;
  stageId: number | string;
  md: number;
  clusterIndex?: number;
  dynamicClusterId?: string;
};

export type HydraulicFractureRenderState = {
  visible: boolean;
  useNeutralColor: boolean;
};

/** Stable scene topology excludes per-step mesh fields and NF branch growth. */
export const fractureRenderTopologyKey = (clusters: FractureRenderCluster[]): string => clusters
  .map((cluster) => `${cluster.clusterId}:${cluster.stageId}:${cluster.perforation.md}`)
  .sort()
  .join('|');

/**
 * Geometry visibility is owned by the committed fracture state, not by the
 * currently selected result property.  A missing/partial property therefore
 * falls back to a neutral colour without making a real fracture disappear.
 */
export const resolveHydraulicFractureRenderState = ({
  show,
  initiated,
  hasGeometry,
  resultAvailable,
}: {
  show: boolean;
  initiated: boolean;
  hasGeometry: boolean;
  resultAvailable: boolean;
}): HydraulicFractureRenderState => ({
  visible: show && initiated && hasGeometry,
  useNeutralColor: !resultAvailable,
});

export type NaturalFractureVisualState =
  | 'UNACTIVATED'
  | 'OPEN'
  | 'CROSS'
  | 'DEFLECT'
  | 'ARREST'
  | 'INTERACTED';

type NaturalFractureInteractionLike = {
  naturalFractureId?: unknown;
  interactionType?: unknown;
  time?: unknown;
};

type NaturalFractureBranchLike = {
  sourceNaturalFractureId?: unknown;
  status?: unknown;
};

const INTERACTION_STATES = new Set<NaturalFractureVisualState>([
  'OPEN', 'CROSS', 'DEFLECT', 'ARREST',
]);

/**
 * Resolve display state exclusively from solver-owned interaction/branch DTOs.
 * A branch in the committed frame proves that the source NF has opened; future
 * interaction events are excluded while replaying an earlier timestep.
 */
export const resolveNaturalFractureVisualState = (
  fractureId: unknown,
  interactions: NaturalFractureInteractionLike[],
  branches: NaturalFractureBranchLike[],
  currentTimeS?: number,
): NaturalFractureVisualState => {
  const id = String(fractureId ?? '');
  if (!id) return 'UNACTIVATED';
  if (branches.some((branch) => String(branch.sourceNaturalFractureId ?? '') === id)) return 'OPEN';

  const eligible = interactions
    .filter((interaction) => String(interaction.naturalFractureId ?? '') === id)
    .filter((interaction) => {
      const eventTime = Number(interaction.time);
      return !Number.isFinite(currentTimeS) || !Number.isFinite(eventTime) || eventTime <= Number(currentTimeS);
    })
    .sort((left, right) => Number(left.time || 0) - Number(right.time || 0));
  const latest = eligible[eligible.length - 1];
  if (!latest) return 'UNACTIVATED';
  const state = String(latest.interactionType ?? '').toUpperCase() as NaturalFractureVisualState;
  return INTERACTION_STATES.has(state) ? state : 'INTERACTED';
};

export const naturalFractureDisplayStyle = (
  state: NaturalFractureVisualState,
  darkScene: boolean,
  passiveColor?: number,
): { color: number; opacity: number; emphasized: boolean } => {
  if (state === 'OPEN') return { color: 0xffb000, opacity: 1, emphasized: true };
  if (state === 'CROSS') return { color: 0x00b8d9, opacity: 0.98, emphasized: true };
  if (state === 'DEFLECT') return { color: 0x59c36a, opacity: 0.98, emphasized: true };
  if (state === 'ARREST') return { color: 0xe55353, opacity: 0.98, emphasized: true };
  if (state === 'INTERACTED') return { color: 0xa66dd4, opacity: 0.95, emphasized: true };
  return {
    color: passiveColor ?? (darkScene ? 0xf1f3f5 : 0x1d2329),
    opacity: darkScene ? 0.72 : 0.58,
    emphasized: false,
  };
};

/**
 * Render one template for every solver cluster. Visibility is resolved from
 * each cluster's real initiation state by the viewer; a stage aggregate is
 * used only for legacy results that contain no cluster DTOs.
 */
export const buildFractureRenderTemplates = (
  stages: FractureRenderStage[],
  playbackClusters: FractureRenderCluster[],
  selectedStage: number | null,
): FractureRenderTemplate[] => {
  const scopedClusters = playbackClusters.filter((cluster) =>
    selectedStage === null || String(cluster.stageId) === String(selectedStage));
  if (scopedClusters.length) {
    return scopedClusters.map((cluster) => ({
      id: cluster.clusterId,
      stageId: cluster.stageId,
      md: cluster.perforation.md,
      clusterIndex: cluster.clusterIndex,
      dynamicClusterId: cluster.clusterId,
    }));
  }
  return stages
    .filter((stage) => selectedStage === null || stage.stage_number === selectedStage)
    .map((stage) => ({
      id: `stage-${stage.stage_number}`,
      stageId: stage.stage_number,
      md: stage.perf_md,
    }));
};
